import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { AlertOctagon, PauseCircle, PlayCircle, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * PauseAiButton — Phase 5.0 emergency kill switch.
 *
 * Sits next to "Go fully autonomous" in Discover Leads. When clicked:
 *  • Opens a confirmation modal (never one-tap by accident)
 *  • POST /api/ai/kill-switch (scope=tenant, optional reason)
 *  • On success, all outbound + cadence + orchestrator actions halt within
 *    ~15s (the security.kill_switch cache TTL) — server-side guards
 *    already wired in event_consumer + cadence_executor
 *  • Users see a persistent "AI paused" state with a "Resume AI" button
 *  • Also shows a red banner if superadmin has triggered a GLOBAL kill switch
 *
 * Independent of GoAutonomousCard so we can drop it in without touching that
 * component's tested logic.
 */
export const PauseAiButton = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/api/ai/kill-switch');
      setStatus(res.data || {});
    } catch (_) { /* non-blocking */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 30s so a superadmin global kill or another admin's pause is
    // reflected in the current session.
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const pauseAI = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/ai/kill-switch', {
        scope: 'tenant',
        reason: reason.trim() || 'Paused by user from Discover Leads',
      });
      toast.success('AI paused — outbound actions will halt within seconds.', { duration: 4000 });
      setConfirmOpen(false);
      setReason('');
      refresh();
    } catch (e) {
      toast.error('Could not pause AI. Please retry.');
    } finally { setSubmitting(false); }
  };

  const resumeAI = async () => {
    setSubmitting(true);
    try {
      await api.delete('/api/ai/kill-switch?scope=all');
      toast.success('AI resumed. Cadences and orchestrator will pick up on the next tick.', { duration: 4000 });
      refresh();
    } catch (e) {
      toast.error('Could not resume AI. Please retry.');
    } finally { setSubmitting(false); }
  };

  if (loading || !status) return null;

  const paused = !!status.tenant_paused;
  const globalPaused = !!status.global_paused;

  return (
    <>
      {/* Global kill (superadmin) — very high priority banner */}
      {globalPaused && (
        <div
          className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-500"
          data-testid="ai-global-killswitch-banner"
        >
          <AlertOctagon className="h-4 w-4 shrink-0" />
          <div className="flex-1">
            <strong>AI is paused platform-wide by a system administrator.</strong>
            {status.global_reason ? <> — {status.global_reason}</> : null}
            {' '}All autonomous actions are halted until the admin resumes.
          </div>
        </div>
      )}

      {/* Tenant kill state — user's own pause */}
      {paused && !globalPaused && (
        <div
          className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-500"
          data-testid="ai-tenant-killswitch-banner"
        >
          <div className="flex items-start gap-2">
            <PauseCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>AI is paused for your workspace.</strong>
              {status.tenant_reason ? <> — {status.tenant_reason}</> : null}
              {status.tenant_activated_by ? (
                <div className="text-[10px] text-amber-500/70 mt-0.5">
                  Paused by {status.tenant_activated_by}
                </div>
              ) : null}
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            className="h-7 text-xs border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
            onClick={resumeAI} disabled={submitting}
            data-testid="ai-resume-btn"
          >
            {submitting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
            Resume AI
          </Button>
        </div>
      )}

      {/* Standard button when nothing is paused */}
      {!paused && !globalPaused && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs border-red-500/30 text-red-500 hover:bg-red-500/10 hover:text-red-500"
          onClick={() => setConfirmOpen(true)}
          data-testid="ai-pause-btn"
        >
          <AlertOctagon className="h-3.5 w-3.5 mr-1.5" />
          Emergency: Pause AI
        </Button>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="ai-pause-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-red-500" />
              Pause ALL AI actions?
            </DialogTitle>
            <DialogDescription className="text-left space-y-2 pt-2">
              <div className="text-xs text-muted-foreground space-y-1">
                <div>• Emails, WhatsApp, LinkedIn and voice sends will halt</div>
                <div>• Cadences will pause (nothing is deleted)</div>
                <div>• Inbound replies still arrive — they just queue safely</div>
                <div>• You can resume anytime with one click</div>
              </div>
              <div className="rounded-md bg-blue-500/10 border border-blue-500/30 p-2 text-[11px] text-blue-500 flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Takes effect within ~15 seconds. Your team + superadmin are notified.
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">
              Reason (optional — helps your team understand)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Investigating a bad reply the AI sent"
              maxLength={500}
              data-testid="ai-pause-reason-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting} data-testid="ai-pause-cancel">
              Cancel
            </Button>
            <Button
              onClick={pauseAI}
              disabled={submitting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="ai-pause-confirm"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <PauseCircle className="h-4 w-4 mr-2" />}
              Pause AI Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PauseAiButton;
